import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { KPI as KPIComponent } from '../../KPI.js';
import useWidgetData from '../hooks/useWidgetData';
import useWidgetConfig from '../hooks/useWidgetConfig';
import { transformWidgetData } from '../config';
import { calculateForecastKPICurrentValue } from '../../sidebar-components/sidebarItemsConfig/helpers.js';
import { getChangeTitle } from '../../Utils.js';
import dayjs from 'dayjs';

/**
 * Helper function to generate cumulative trend data from items grouped by date
 * @param {Array} items - Array of items with created_at date
 * @param {Function} getValue - Function to extract value from each item (e.g., (item) => item.costs or (item) => 1)
 * @returns {Array|null} - Cumulative trend data array, or null if no items
 */
const generateCumulativeTrendData = (items, getValue) => {
  if (!items || items.length === 0) {
    return null;
  }

  // Validate that getValue is a function
  if (typeof getValue !== 'function') {
    console.error('generateCumulativeTrendData: getValue must be a function', { getValue });
    return null;
  }

  // Group items by created_at date (day level)
  const valuesByDate = {};
  items.forEach((item) => {
    if (item.created_at) {
      const dateObj = dayjs(item.created_at);
      // Validate that the date is valid before using it
      if (!dateObj.isValid()) {
        console.warn('generateCumulativeTrendData: Invalid date encountered', { 
          created_at: item.created_at,
          item 
        });
        return; // Skip this item
      }
      
      const dateKey = dateObj.format('YYYY-MM-DD');
      // Coerce getValue(item) to a number to avoid NaN propagation
      const value = Number(getValue(item)) || 0;
      valuesByDate[dateKey] = (valuesByDate[dateKey] || 0) + value;
    }
  });

  // Get all dates and sort them
  const sortedDates = Object.keys(valuesByDate).sort();

  // Create cumulative array (running total)
  let cumulativeTotal = 0;
  return sortedDates.map((date) => {
    cumulativeTotal += valuesByDate[date];
    return cumulativeTotal;
  });
};

/**
 * KPI Widget - Wraps the existing KPI component with widget functionality
 * Supports both static and API data sources
 * Uses the useWidgetConfig hook for centralized config handling
 */
const KPIWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false,
  onFilterAdd 
}) => {
  // For totalCost widgets, ensure group_by_date is true to get time-series data for trendline
  const isTotalCostWidget = config?.transformType === 'totalCost';
  
  // Use centralized config hook with group_by_date and add_detailed_results override for totalCost widgets
  // These params ensure the API returns time-series data with groupings structure (same as Analyzer view)
  // date_granularity: "Day" is required when group_by_date is true
  const { finalConfig, stableFilters, startDate, endDate } = useWidgetConfig(config, filters, {
    includeDates: true,
    additionalBody: isTotalCostWidget ? { 
      group_by_date: true, 
      add_detailed_results: true,
      date_granularity: "Day"
    } : {}
  });

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Check if this is a forecast KPI widget (has CloudCost.details in data)
  const isForecastKPI = useMemo(() => {
    return data && data.CloudCost && data.CloudCost.details && Array.isArray(data.CloudCost.details);
  }, [data]);

  // Transform data with specialized logic for different KPI types
  const widgetData = useMemo(() => {
    if (!data) return null;

    // Check transformType FIRST - it takes precedence over data-based detection
    // Total Cost KPI: Sum grouped_totals
    if (config?.transformType === 'totalCost') {
      const grouped_totals = data?.CloudCost?.grouped_totals || data?.grouped_totals || [];
      let currentPeriod = 0;
      let previousPeriod = 0;

      if (Array.isArray(grouped_totals) && grouped_totals.length > 0) {
        grouped_totals.forEach((group) => {
          const curr = Number(group.current_period);
          const prev = Number(group.previous_period);
          if (!isNaN(curr)) currentPeriod += curr;
          if (!isNaN(prev)) previousPeriod += prev;
        });
      } else {
        // Fallback: calculate from details
        const details = data?.CloudCost?.details || data?.details || [];
        if (details.length > 0) {
          details.forEach((detail) => {
            const measure = detail.measures?.[0]?.value || detail.measure || detail.value || 0;
            const val = Number(measure);
            if (!isNaN(val)) currentPeriod += val;
          });
        }
      }

      // Generate trend data if showTrendline is enabled (default true)
      let trendData = null;
      if (config?.showTrendline !== false) {
        const details = data?.CloudCost?.details || data?.details || [];
        
        // Group costs by date
        const costsByDate = {};
        details.forEach((detail) => {
          let time, measure;
          
          if (detail.groupings && Array.isArray(detail.groupings)) {
            time = detail.groupings[0]?.value || detail.time;
            measure = detail.measures?.[0]?.value ?? detail.measure ?? 0;
          } else {
            time = detail.time;
            measure = detail.measures?.[0]?.value || detail.measure || detail.value || 0;
          }
          
          if (time) {
            const itemDate = dayjs(time);
            // Only create dateKey/accumulate measure when date is valid
            if (itemDate.isValid()) {
              const dateKey = itemDate.format('YYYY-MM-DD');
              costsByDate[dateKey] = (costsByDate[dateKey] || 0) + (measure || 0);
            }
          }
        });
        
        // Get all dates and sort them
        const sortedDates = Object.keys(costsByDate).sort();
        
        // Create array for SparkLine (simple values array)
        // Only set trendData if we have actual data points
        if (sortedDates.length > 0) {
          trendData = sortedDates.map((date) => costsByDate[date]);
        }
      }

      return {
        currentValue: currentPeriod,
        previousValue: previousPeriod,
        trendData
      };
    }

    // Forecast KPI: Calculate currentValue for selected product (no series filtering - sum all resources)
    if (isForecastKPI) {
      // For forecast KPI with product selection, sum all resources within the product
      // The API already filters by product via category/productID params, so we don't filter by series
      const currentValue = calculateForecastKPICurrentValue(
        data,
        null // No series filtering - show total for all resources in the selected product
      );
      
      // Calculate previous month's actual cost for change comparison
      let previousValue = null;
      if (config?.showChange === true) {
        const details = data?.CloudCost?.details || [];
        const now = dayjs();
        const previousMonth = now.subtract(1, 'month').format('YYYY-MM');
        
        let previousMonthActual = 0;
        details.forEach((d) => {
          let time, measure;
          
          if (d.groupings && Array.isArray(d.groupings)) {
            time = d.groupings[0]?.value || d.time;
            measure = d.measures?.[0]?.value ?? d.measure ?? 0;
          } else {
            time = d.time;
            measure = d.measure ?? 0;
          }
          
          const itemDate = dayjs(time);
          if (itemDate.isValid() && itemDate.format('YYYY-MM') === previousMonth) {
            previousMonthActual += (measure || 0);
          }
        });
        
        previousValue = previousMonthActual;
      }
      
      // Generate trend data if showTrendline is enabled (default true)
      let trendData = null;
      if (config?.showTrendline !== false) {
        const details = data?.CloudCost?.details || [];

        // Determine which months to include based on config
        const now = dayjs();
        const currentMonthStart = now.startOf('month');
        const currentMonthEnd = now.endOf('month');
        const previousMonthStart = currentMonthStart.subtract(1, 'month');
        const previousMonthEnd = previousMonthStart.endOf('month');
        const nextMonthStart = currentMonthStart.add(1, 'month');
        const nextMonthEnd = nextMonthStart.endOf('month');

        const showPreviousMonth = config?.showPreviousMonth === true;
        const showNextMonth = config?.showNextMonth === true;
        // Group by date and sum actual and forecast separately for each day
        const costsByDate = {};
        details.forEach((d) => {
          let time, measure, forecast;
          
          if (d.groupings && Array.isArray(d.groupings)) {
            time = d.groupings[0]?.value || d.time;
            measure = d.measures?.[0]?.value ?? d.measure ?? 0;
            forecast = d.measures?.[1]?.value ?? d.forecast ?? 0;
          } else {
            time = d.time;
            measure = d.measure ?? 0;
            forecast = d.forecast ?? 0;
          }
          
          if (!time) return;

          const parsedDate = dayjs(time);
          // Validate parsed date before using it as a key to avoid "Invalid Date"
          if (!parsedDate.isValid()) {
            return; // Skip this detail entry
          }

          // Filter dates based on month visibility settings using timestamp comparisons
          const timeValue = parsedDate.valueOf();
          const inPreviousMonth =
            timeValue >= previousMonthStart.valueOf() &&
            timeValue <= previousMonthEnd.valueOf();
          const inCurrentMonth =
            timeValue >= currentMonthStart.valueOf() &&
            timeValue <= currentMonthEnd.valueOf();
          const inNextMonth =
            timeValue >= nextMonthStart.valueOf() &&
            timeValue <= nextMonthEnd.valueOf();

          if (
            (!inCurrentMonth) &&
            !(showPreviousMonth && inPreviousMonth) &&
            !(showNextMonth && inNextMonth)
          ) {
            return;
          }

          const dateKey = parsedDate.format('YYYY-MM-DD');
          if (!costsByDate[dateKey]) {
            costsByDate[dateKey] = { actual: 0, forecast: 0 };
          }
          costsByDate[dateKey].actual += (measure || 0);
          costsByDate[dateKey].forecast += (forecast || 0);
        });
        
        // Get all dates and sort them
        const sortedDates = Object.keys(costsByDate).sort();

        // Apply separation logic so forecast starts when actual stops
        let lastActualDateIndex = -1;
        sortedDates.forEach((date, index) => {
          const { actual } = costsByDate[date];
          if (actual !== null && actual !== undefined && actual !== 0) {
            lastActualDateIndex = index;
          }
        });

        // Create array with actual/forecast structure for SparkLine
        // Preserve legitimate zero values; only treat strictly null/undefined as missing (null)
        trendData = sortedDates.map((date, index) => {
          const { actual, forecast } = costsByDate[date];

          // Actual line: show up to and including the last actual date
          const actualValue =
            lastActualDateIndex === -1 || index <= lastActualDateIndex
              ? (actual === null || actual === undefined ? null : actual)
              : null;

          // Forecast line: start after the last actual date
          const forecastValue =
            lastActualDateIndex !== -1 && index > lastActualDateIndex
              ? (forecast === null || forecast === undefined ? null : forecast)
              : null;

        return {
            actual: actualValue,
            forecast: forecastValue
          };
        });
      }
      
      return { currentValue, previousValue, trendData, isForecast: true };
    }

    // Insights Savings KPI: Sum costs from insights
    if (config?.transformType === 'insightsSavings') {
      const items = Array.isArray(data?.Insights) ? data.Insights : [];
      const total = items.reduce((sum, it) => sum + (it.costs || 0), 0);
      
      // Generate trend data if showTrendline is enabled (default true)
      const trendData = (config?.showTrendline !== false)
        ? generateCumulativeTrendData(items, (item) => item.costs || 0)
        : null;
      
      return { 
        currentValue: total,
        trendData 
      };
    }

    // Insights Count KPI: Count total number of insights
    if (config?.transformType === 'insightsCount') {
      const items = Array.isArray(data?.Insights) ? data.Insights : [];
      const currentValue = items.length;
      
      // Generate trend data if showTrendline is enabled (default true)
      const trendData = (config?.showTrendline !== false)
        ? generateCumulativeTrendData(items, () => 1)
        : null;
      
      return { 
        currentValue,
        trendData 
      };
    }

    // Standard KPI: Use default transform
    return transformWidgetData('kpi', data);
  }, [
    data,
    isForecastKPI,
    config?.transformType,
    config?.showChange,
    config?.showTrendline,
    config?.showPreviousMonth,
    config?.showNextMonth
  ]);

  // Build display title - include selected product for forecast KPIs
  const displayTitle = useMemo(() => {
    if (isForecastKPI && config?.selectedProduct?.label) {
      return `Forecast for ${config.selectedProduct.label}`;
    }
    return config.title;
  }, [isForecastKPI, config?.title, config?.selectedProduct?.label]);

  // Calculate changeTitle based on period for cost KPI and forecast KPI
  const changeTitle = useMemo(() => {
    // If config already has a changeTitle, use it (allows manual override)
    if (config?.changeTitle) {
      return config.changeTitle;
    }

    // For forecast KPI: always compare current month vs previous month
    if (isForecastKPI) {
      return "vs previous month";
    }

    // For cost KPI (totalCost): calculate based on date range from filters
    if (config?.transformType === 'totalCost' && startDate && endDate) {
      // startDate and endDate are already dayjs objects from useFilterStoreWithDayjsDates
      return getChangeTitle(startDate, endDate);
    }

    // Default fallback
    return "vs previous month";
  }, [config?.changeTitle, config?.transformType, isForecastKPI, startDate, endDate]);

  // Handle filter events from KPI interactions
  const handleKPIInteraction = (interactionData) => {
    if (onFilterAdd && interactionData) {
      onFilterAdd(interactionData);
    }
  };

  return (
    <BaseWidget
      widgetId={widgetId}
      title={displayTitle}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      {widgetData && (
        <KPIComponent
          title={displayTitle}
          currentValue={widgetData.currentValue}
          previousValue={widgetData.previousValue}
          format={config.format}
          showChange={config.showChange}
          trendData={config?.showTrendline !== false ? widgetData.trendData : null}
          changeTitle={changeTitle}
          onInteraction={handleKPIInteraction}
          showForecast={widgetData.isForecast}
        />
      )}
    </BaseWidget>
  );
};

KPIWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    format: PropTypes.oneOf(['currency', 'percentage', 'decimal']).isRequired,
    showChange: PropTypes.bool,
    changeTitle: PropTypes.string,
    showTrendline: PropTypes.bool,
    transformType: PropTypes.oneOf(['totalCost', 'insightsSavings', 'insightsCount']),
    selectedProduct: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      category: PropTypes.string,
      series: PropTypes.string,
      category_description: PropTypes.string
    }),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  onFilterAdd: PropTypes.func
};

export default KPIWidget;
